import React, { useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { Close, Delete, Edit } from "@mui/icons-material";
import { post, put, remove } from "../../redux/ApiHelper";
import AiQuestionPaperGeneratorSheet from "./AiQuestionPaperGeneratorSheet";
import { QuestionInterface } from "../../interfaces/question.interface";
import { QuestionPaperInterface } from "../../interfaces/question-paper.interface";
import { joinSubjectChips, normalizeSubjectList } from "../../utils/subjectChips.util";
import { errorToast } from "../../utils/toast.util";

type ErrorsShape = Record<string, string | undefined>;

type PropsBase = {
  paperSxBg?: string;
};

type QuestionPaperFormDialogProps = PropsBase & {
  open: boolean;
  mode: "create" | "edit";
  onClose: () => void;
  onSubmit: () => void;
  loading?: boolean;
  questionPaperId?: string;
  signedAdmin?: { email: string; role: string; type: string };
  onPaperUpdated?: (paper: QuestionPaperInterface) => void;

  subjects: string[];
  availableQuestions: any[];
  formData: any;
  setFormData: (next: any) => void;
  errors: ErrorsShape;
  setErrors: (next: ErrorsShape) => void;

  onToggleQuestion: (questionId: string) => void;
  onGoToQuestionsPage: () => void;

  extraContentBeforeQuestions?: React.ReactNode;
  submitText?: string;
  titleText?: string;
  createExtras?: {
    examinationCreationRequestId?: string | null;
    tuitionPeriodId?: string | null;
  };
};

export function QuestionPaperFormDialog({
  open,
  mode,
  onClose,
  onSubmit,
  loading,
  questionPaperId,
  signedAdmin,
  onPaperUpdated,
  subjects,
  availableQuestions,
  formData,
  setFormData,
  errors,
  setErrors,
  onToggleQuestion,
  onGoToQuestionsPage,
  extraContentBeforeQuestions,
  submitText,
  titleText,
  createExtras,
}: QuestionPaperFormDialogProps) {
  const isEdit = mode === "edit";
  const [aiSheetOpen, setAiSheetOpen] = useState(false);
  const [quickEditOpen, setQuickEditOpen] = useState(false);
  const [quickEditQuestion, setQuickEditQuestion] = useState<QuestionInterface | null>(null);
  const [savingQuickEdit, setSavingQuickEdit] = useState(false);
  const [marksConfirmOpen, setMarksConfirmOpen] = useState(false);
  const classOptions = useMemo(
    () => ["Nursery", "LKG", "UKG", ...Array.from({ length: 12 }).map((_, i) => `Class ${i + 1}`)],
    []
  );

  const selectedQuestions: QuestionInterface[] = useMemo(() => {
    const raw = Array.isArray(formData?.questions) ? formData.questions : [];
    return raw
      .map((q: any) => {
        if (q && typeof q === "object") return q as QuestionInterface;
        const found = availableQuestions?.find((aq: any) => aq?._id === q);
        return found || null;
      })
      .filter(Boolean);
  }, [formData?.questions, availableQuestions]);

  /** AI generate API needs a persisted question-paper id */
  const canGenerateAi = !!signedAdmin;
  const [questionBuildMode, setQuestionBuildMode] = useState<"manual" | "ai">("manual");
  const [aiTargetPaperId, setAiTargetPaperId] = useState<string | null>(questionPaperId || null);
  const [creatingForAi, setCreatingForAi] = useState(false);

  const isLevelledPaper = formData?.examType === "regular" || formData?.examType === "puzzle solver";
  const isMandatoryPaper = formData?.examType === "mandatory";
  const subjectChips = useMemo(
    () => normalizeSubjectList(formData?.subject),
    [formData?.subject]
  );
  const subjectOptions = useMemo(() => {
    const seen = new Set(subjectChips.map((s) => s.toLowerCase()));
    return [
      ...subjectChips,
      ...subjects.filter((s) => {
        const key = String(s).toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      }),
    ];
  }, [subjects, subjectChips]);
  const safeProgressLevel = Number.isFinite(Number(formData?.progressLevel)) ? Number(formData?.progressLevel) : 1;
  const examTypeLabel = String(formData?.examType || "").trim();
  const isPuzzleSolver = formData?.examType === "puzzle solver";

  const selectedTotalMarks = useMemo(
    () => selectedQuestions.reduce((sum, q) => sum + (Number(q.marks) || 0), 0),
    [selectedQuestions]
  );
  const fullMarks = Number(formData?.fullMarks) || 0;
  const markDelta = fullMarks - selectedTotalMarks;
  const marksStatus =
    fullMarks <= 0
      ? { label: "Set full marks to compare", tone: "muted" as const }
      : markDelta === 0
        ? { label: "Perfect match", tone: "success" as const }
        : markDelta > 0
          ? { label: `${markDelta} marks remaining`, tone: "info" as const }
          : { label: `Over by ${Math.abs(markDelta)} marks`, tone: "warning" as const };

  const makeTextFieldDarkSx = (hasError?: boolean) =>
    ({
      "& .MuiOutlinedInput-root": {
        color: "#ffffff",
        "& fieldset": {
          borderColor: hasError ? "#ff0000" : "rgba(255,255,255,0.65)",
        },
        "&:hover fieldset": {
          borderColor: hasError ? "#ff0000" : "rgba(255,255,255,0.85)",
        },
        "&.Mui-focused fieldset": {
          borderColor: hasError ? "#ff0000" : "#fcd307",
        },
      },
      "& .MuiInputLabel-root": {
        color: "#ffffff",
        "&.Mui-focused": { color: "#ffffff" },
      },
      "& .MuiFormHelperText-root": { color: "#ff0000" },
    }) as const;

  const makeSelectDarkSx = (hasError?: boolean) =>
    ({
      color: "#ffffff",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: hasError ? "#ff0000" : "rgba(255,255,255,0.65)",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: hasError ? "#ff0000" : "rgba(255,255,255,0.85)",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: hasError ? "#ff0000" : "#fcd307",
      },
      "& .MuiSvgIcon-root": { color: "#ffffff" },
    }) as const;

  const shouldWarnMarksMismatch = fullMarks > 0 && selectedTotalMarks !== fullMarks;
  const handleFinalSubmit = () => {
    if (shouldWarnMarksMismatch) {
      setMarksConfirmOpen(true);
      return;
    }
    onSubmit();
  };

  const collectRequiredFieldErrors = (): ErrorsShape => {
    const next: ErrorsShape = {};
    if (!String(formData?.title || "").trim()) next.title = "Title is required";
    if (!isPuzzleSolver && subjectChips.length === 0) next.subject = "Subject is required";
    if (!String(formData?.class || "").trim()) next.class = "Class is required";
    const needsStart =
      formData?.examType !== "regular" && formData?.examType !== "puzzle solver";
    if (needsStart && !formData?.examStartDateTime) {
      next.examStartDateTime = "Exam start date and time is required";
    }
    if (!formData?.durationInMinutes || Number(formData.durationInMinutes) <= 0) {
      next.durationInMinutes = "Duration must be greater than 0";
    }
    if (!formData?.fullMarks || Number(formData.fullMarks) <= 0) {
      next.fullMarks = "Full marks must be greater than 0";
    }
    if (!formData?.passMarks || Number(formData.passMarks) <= 0) {
      next.passMarks = "Pass marks must be greater than 0";
    } else if (
      Number(formData.fullMarks) > 0 &&
      Number(formData.passMarks) > Number(formData.fullMarks)
    ) {
      next.passMarks = "Pass marks cannot be greater than full marks";
    }
    return next;
  };

  async function ensurePaperIdForAi(): Promise<string | null> {
    if (!signedAdmin) return null;

    const fieldErrors = collectRequiredFieldErrors();
    setErrors(fieldErrors);
    if (Object.values(fieldErrors).some(Boolean)) return null;

    if (isMandatoryPaper && !questionPaperId && !createExtras?.examinationCreationRequestId) {
      errorToast("Examination creation request ID is required for mandatory exam papers");
      return null;
    }

    if (aiTargetPaperId) return aiTargetPaperId;
    if (questionPaperId) {
      setAiTargetPaperId(questionPaperId);
      return questionPaperId;
    }

    try {
      setCreatingForAi(true);
      const payload: any = {
        ...formData,
        questions: [],
        adminEmail: signedAdmin.email,
        adminRole: signedAdmin.role,
        adminType: signedAdmin.type,
        ...(createExtras?.examinationCreationRequestId
          ? { examinationCreationRequestId: createExtras.examinationCreationRequestId }
          : {}),
        ...(createExtras?.tuitionPeriodId
          ? { tuitionPeriodId: createExtras.tuitionPeriodId }
          : {}),
      };
      const createdResp = (await post("/admin/examination/question-paper/create", payload)) as {
        data?: QuestionPaperInterface;
      };
      const created = createdResp?.data as any;
      const createdId = created?._id ? String(created._id) : null;
      if (!createdId) return null;

      setAiTargetPaperId(createdId);
      onPaperUpdated?.(created as QuestionPaperInterface);
      return createdId;
    } catch {
      return null;
    } finally {
      setCreatingForAi(false);
    }
  }

  async function handleResetQuestions() {
    if (!questionPaperId) return;
    const resp = await remove(`/admin/examination/question-paper/${questionPaperId}/questions`);
    if (resp?.data) {
      setFormData({ ...formData, questions: resp.data.questions || [] });
      onPaperUpdated?.(resp.data as QuestionPaperInterface);
    }
  }

  async function handleRemoveOne(questionId: string) {
    if (!questionPaperId) return;
    const resp = await remove(
      `/admin/examination/question-paper/${questionPaperId}/questions/${questionId}`
    );
    if (resp?.data) {
      setFormData({ ...formData, questions: resp.data.questions || [] });
      onPaperUpdated?.(resp.data as QuestionPaperInterface);
    }
  }

  function openQuickEdit(q: QuestionInterface) {
    setQuickEditQuestion(q);
    setQuickEditOpen(true);
  }

  async function saveQuickEdit() {
    if (!quickEditQuestion?._id || !signedAdmin) return;
    try {
      setSavingQuickEdit(true);
      const payload = {
        question: quickEditQuestion.question,
        options: quickEditQuestion.options || [],
        correctOptionIndexes: quickEditQuestion.correctOptionIndexes || [],
        marks: quickEditQuestion.marks,
        negativeMarks: quickEditQuestion.negativeMarks || 0,
        difficultyLevel: quickEditQuestion.difficultyLevel,
        subject: quickEditQuestion.subject,
        class: quickEditQuestion.class,
        questionType: quickEditQuestion.questionType,
        adminEmail: signedAdmin.email,
        adminRole: signedAdmin.role,
        adminType: signedAdmin.type,
      };

      const resp = await put(`/admin/examination/question/${quickEditQuestion._id}`, payload);
      const updated = resp?.data as QuestionInterface | undefined;
      if (updated?._id) {
        const nextQs = selectedQuestions.map((q) => (q._id === updated._id ? { ...q, ...updated } : q));
        setFormData({ ...formData, questions: nextQs });
      }
      setQuickEditOpen(false);
      setQuickEditQuestion(null);
    } finally {
      setSavingQuickEdit(false);
    }
  }

  return (
    <>
      <Dialog
        open={open && !aiSheetOpen && !quickEditOpen}
        onClose={onClose}
        fullScreen
        PaperProps={{
          sx: {
            backgroundColor: "#171717",
            color: "#ffffff",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle
          sx={{
            color: "#ffffff",
            pr: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            borderBottom: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" noWrap sx={{ color: "#ffffff", fontWeight: 800 }}>
              {titleText ?? (isEdit ? "Edit Question Paper" : "Create Question Paper")}
            </Typography>
            {examTypeLabel ? (
              <Box sx={{ mt: 0.75 }}>
                <Chip
                  label={examTypeLabel}
                  size="small"
                  sx={{
                    bgcolor: "rgba(252, 211, 7, 0.14)",
                    color: "#ffffff",
                    border: "1px solid rgba(252, 211, 7, 0.35)",
                    fontWeight: 800,
                    textTransform: "capitalize",
                  }}
                  variant="outlined"
                />
              </Box>
            ) : null}
          </Box>
          <IconButton onClick={onClose} sx={{ color: "rgba(255,255,255,0.85)" }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            backgroundColor: "#171717",
            color: "#ffffff",
            p: 0,
            flex: 1,
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              height: "100%",
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "420px 1fr" },
              minHeight: 0,
            }}
          >
            <Box
              sx={{
                minHeight: 0,
                overflow: "auto",
                p: 2,
                borderRight: { xs: "none", md: "1px solid rgba(255,255,255,0.08)" },
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {signedAdmin ? (
                  <>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                      <Typography variant="subtitle2" sx={{ color: "#ffffff" }}>
                        Question source
                      </Typography>
                      <ToggleButtonGroup
                        exclusive
                        value={questionBuildMode}
                        onChange={(_, v) => v && setQuestionBuildMode(v)}
                        sx={{
                          "& .MuiToggleButton-root": {
                            color: "rgba(255,255,255,0.85)",
                            borderColor: "rgba(255,255,255,0.25)",
                            textTransform: "none",
                            fontWeight: 700,
                          },
                          "& .Mui-selected": {
                            backgroundColor: "rgba(252, 211, 7, 0.22) !important",
                            color: "#ffffff !important",
                            borderColor: "#fcd307 !important",
                          },
                        }}
                      >
                        <ToggleButton value="manual">Manual selection</ToggleButton>
                        <ToggleButton value="ai">Generate by AI</ToggleButton>
                      </ToggleButtonGroup>

                      {questionBuildMode === "ai" ? (
                        <Alert
                          severity="info"
                          sx={{
                            bgcolor: "rgba(252, 211, 7, 0.08)",
                            color: "#ffffff",
                            border: "1px solid rgba(252, 211, 7, 0.35)",
                            "& .MuiAlert-icon": { color: "#fcd307" },
                          }}
                        >
                          Click <strong>Generate by AI</strong>. We’ll auto-save this paper if needed, then attach the generated
                          questions.
                        </Alert>
                      ) : null}
                    </Box>
                  </>
                ) : null}

                <TextField
                  fullWidth
                  label="Title"
                  value={formData.title}
                  onChange={(e) => {
                    setFormData({ ...formData, title: e.target.value });
                    if (errors.title) setErrors({ ...errors, title: undefined });
                  }}
                  error={!!errors.title}
                  helperText={errors.title}
                  required
                  sx={makeTextFieldDarkSx(!!errors.title)}
                />

                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={2}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  sx={makeTextFieldDarkSx(false)}
                />

                {isPuzzleSolver ? (
                  <Box
                    sx={{
                      border: "1px dashed rgba(255,255,255,0.18)",
                      borderRadius: 1.5,
                      p: 1.25,
                      backgroundColor: "rgba(255,255,255,0.03)",
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)" }}>
                      Subject
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#ffffff", fontWeight: 800 }}>
                      puzzle
                    </Typography>
                  </Box>
                ) : isMandatoryPaper ? (
                  <Autocomplete
                    multiple
                    freeSolo
                    options={subjectOptions}
                    value={subjectChips}
                    onChange={(_e, value) => {
                      const chips = normalizeSubjectList(value as string[]);
                      setFormData({ ...formData, subject: joinSubjectChips(chips) });
                      if (errors.subject) setErrors({ ...errors, subject: undefined });
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={`${option}-${index}`}
                          label={option}
                          size="small"
                          sx={{
                            backgroundColor: "rgba(252, 211, 7, 0.18)",
                            color: "#ffffff",
                            "& .MuiChip-deleteIcon": { color: "rgba(255,255,255,0.7)" },
                          }}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Subjects"
                        required
                        error={!!errors.subject}
                        helperText={errors.subject || "Auto-filled from tuition period. Add or remove chips as needed."}
                        sx={makeTextFieldDarkSx(!!errors.subject)}
                      />
                    )}
                    sx={{
                      "& .MuiAutocomplete-inputRoot": { color: "#ffffff" },
                      "& .MuiSvgIcon-root": { color: "#ffffff" },
                    }}
                  />
                ) : (
                  <FormControl fullWidth error={!!errors.subject} required>
                    <InputLabel sx={{ color: "#ffffff" }}>Subject</InputLabel>
                    <Select
                      value={formData.subject}
                      label="Subject"
                      onChange={(e) => {
                        setFormData({ ...formData, subject: e.target.value });
                        if (errors.subject) setErrors({ ...errors, subject: undefined });
                      }}
                      sx={makeSelectDarkSx(!!errors.subject)}
                    >
                      {subjects.map((subject) => (
                        <MenuItem key={subject} value={subject} sx={{ color: "#000000" }}>
                          {subject}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.subject && (
                      <Typography variant="caption" sx={{ color: "#ff0000", mt: 0.5, ml: 1.75 }}>
                        {errors.subject}
                      </Typography>
                    )}
                  </FormControl>
                )}

                <FormControl fullWidth error={!!(errors as any).class} required>
                  <InputLabel sx={{ color: "#ffffff" }}>Class</InputLabel>
                  <Select
                    value={formData.class || ""}
                    label="Class"
                    onChange={(e) => {
                      setFormData({ ...formData, class: e.target.value });
                      if ((errors as any).class) setErrors({ ...(errors as any), class: undefined });
                    }}
                    sx={makeSelectDarkSx(!!(errors as any).class)}
                  >
                    {classOptions.map((c) => (
                      <MenuItem key={c} value={c} sx={{ color: "#000000" }}>
                        {c}
                      </MenuItem>
                    ))}
                  </Select>
                  {(errors as any).class && (
                    <Typography variant="caption" sx={{ color: "#ff0000", mt: 0.5, ml: 1.75 }}>
                      {(errors as any).class}
                    </Typography>
                  )}
                </FormControl>

                {isLevelledPaper ? (
                  <>
                    <TextField
                      fullWidth
                      type="number"
                      label="Progress level (1-100)"
                      value={safeProgressLevel}
                      onChange={(e) => {
                        const next = e.target.value === "" ? 1 : Number(e.target.value);
                        setFormData({ ...formData, progressLevel: next });
                        if ((errors as any).progressLevel) setErrors({ ...(errors as any), progressLevel: undefined });
                      }}
                      inputProps={{ min: 1, max: 100, step: 1 }}
                      error={!!(errors as any).progressLevel}
                      helperText={(errors as any).progressLevel}
                      required
                      sx={makeTextFieldDarkSx(!!(errors as any).progressLevel)}
                    />

                    {!isEdit ? (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!formData.createNextLevelIfExists}
                            onChange={(e) =>
                              setFormData({ ...formData, createNextLevelIfExists: e.target.checked })
                            }
                            sx={{ color: "#ffffff", "&.Mui-checked": { color: "#fcd307" } }}
                          />
                        }
                        label={
                          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.92)" }}>
                            Create next level automatically if this level already exists
                          </Typography>
                        }
                      />
                    ) : null}
                  </>
                ) : null}

                {!isLevelledPaper ? (
                  <TextField
                    fullWidth
                    type="datetime-local"
                    label="Exam Start Date & Time"
                    value={formData.examStartDateTime}
                    onChange={(e) => {
                      setFormData({ ...formData, examStartDateTime: e.target.value });
                      if (errors.examStartDateTime) {
                        setErrors({ ...errors, examStartDateTime: undefined });
                      }
                    }}
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.examStartDateTime}
                    helperText={errors.examStartDateTime}
                    required
                    sx={makeTextFieldDarkSx(!!errors.examStartDateTime)}
                  />
                ) : null}

                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    type="number"
                    label="Duration (minutes)"
                    value={formData.durationInMinutes || ""}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        durationInMinutes: e.target.value === "" ? 0 : Number(e.target.value),
                      });
                      if (errors.durationInMinutes) {
                        setErrors({ ...errors, durationInMinutes: undefined });
                      }
                    }}
                    error={!!errors.durationInMinutes}
                    helperText={errors.durationInMinutes}
                    required
                    sx={{
                      ...makeTextFieldDarkSx(!!errors.durationInMinutes),
                      "& input[type=number]": { "-moz-appearance": "textfield" as any },
                      "& input[type=number]::-webkit-outer-spin-button": {
                        "-webkit-appearance": "none",
                        margin: 0,
                      },
                      "& input[type=number]::-webkit-inner-spin-button": {
                        "-webkit-appearance": "none",
                        margin: 0,
                      },
                    }}
                  />

                  <TextField
                    type="number"
                    label="Full Marks"
                    value={formData.fullMarks || ""}
                    onChange={(e) => {
                      const nextFull = e.target.value === "" ? 0 : Number(e.target.value);
                      setFormData({ ...formData, fullMarks: nextFull });
                      if (errors.fullMarks) setErrors({ ...errors, fullMarks: undefined });
                      if (errors.passMarks && formData.passMarks > nextFull) {
                        setErrors({ ...errors, passMarks: "Pass marks cannot be greater than full marks" });
                      }
                    }}
                    error={!!errors.fullMarks}
                    helperText={errors.fullMarks}
                    required
                    sx={{
                      ...makeTextFieldDarkSx(!!errors.fullMarks),
                      "& input[type=number]": { "-moz-appearance": "textfield" as any },
                      "& input[type=number]::-webkit-outer-spin-button": {
                        "-webkit-appearance": "none",
                        margin: 0,
                      },
                      "& input[type=number]::-webkit-inner-spin-button": {
                        "-webkit-appearance": "none",
                        margin: 0,
                      },
                    }}
                  />

                  <TextField
                    type="number"
                    label="Pass Marks"
                    value={formData.passMarks || ""}
                    onChange={(e) => {
                      setFormData({ ...formData, passMarks: e.target.value === "" ? 0 : Number(e.target.value) });
                      if (errors.passMarks) setErrors({ ...errors, passMarks: undefined });
                    }}
                    error={!!errors.passMarks}
                    helperText={errors.passMarks}
                    required
                    sx={{
                      ...makeTextFieldDarkSx(!!errors.passMarks),
                      "& input[type=number]": { "-moz-appearance": "textfield" as any },
                      "& input[type=number]::-webkit-outer-spin-button": {
                        "-webkit-appearance": "none",
                        margin: 0,
                      },
                      "& input[type=number]::-webkit-inner-spin-button": {
                        "-webkit-appearance": "none",
                        margin: 0,
                      },
                    }}
                  />
                </Box>

                {extraContentBeforeQuestions ? (
                  <>
                    <Divider sx={{ borderColor: "#ffffff", my: 2 }} />
                    {extraContentBeforeQuestions}
                    <Divider sx={{ borderColor: "#ffffff", my: 2 }} />
                  </>
                ) : null}
              </Box>
            </Box>

            <Box sx={{ minHeight: 0, overflow: "auto", p: 2 }}>
              <Box
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                  pb: 1.5,
                  mb: 2,
                  borderBottom: "1px solid rgba(255,255,255,0.08)",
                  backgroundColor: "#171717",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "flex-end",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 800 }}>
                      Marks summary
                    </Typography>
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.72)" }}>
                      Selected vs full marks
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                    <Chip
                      label={`Selected: ${selectedTotalMarks}`}
                      sx={{
                        bgcolor: "rgba(255,255,255,0.08)",
                        color: "#ffffff",
                        border: "1px solid rgba(255,255,255,0.14)",
                      }}
                      variant="outlined"
                    />
                    <Chip
                      label={`Full: ${fullMarks || "—"}`}
                      sx={{
                        bgcolor: "rgba(255,255,255,0.08)",
                        color: "#ffffff",
                        border: "1px solid rgba(255,255,255,0.14)",
                      }}
                      variant="outlined"
                    />
                    <Chip
                      label={marksStatus.label}
                      sx={{
                        bgcolor:
                          marksStatus.tone === "success"
                            ? "rgba(76, 175, 80, 0.18)"
                            : marksStatus.tone === "warning"
                              ? "rgba(255, 193, 7, 0.16)"
                              : marksStatus.tone === "info"
                                ? "rgba(33, 150, 243, 0.14)"
                                : "rgba(255,255,255,0.08)",
                        color: "#ffffff",
                        border:
                          marksStatus.tone === "success"
                            ? "1px solid rgba(76, 175, 80, 0.35)"
                            : marksStatus.tone === "warning"
                              ? "1px solid rgba(255, 193, 7, 0.35)"
                              : marksStatus.tone === "info"
                                ? "1px solid rgba(33, 150, 243, 0.30)"
                                : "1px solid rgba(255,255,255,0.14)",
                      }}
                      variant="outlined"
                    />
                  </Box>
                </Box>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {signedAdmin && questionBuildMode === "ai" && canGenerateAi ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1.5,
                      flexWrap: "wrap",
                      border: "1px solid rgba(255,255,255,0.12)",
                      borderRadius: 1.5,
                      p: 1.5,
                      backgroundColor: "rgba(255,255,255,0.03)",
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 700 }}>
                        AI question generator
                      </Typography>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>
                        Opens a bottom sheet. Generates MCQs, stores them, and attaches to this paper.
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <Button
                        onClick={async () => {
                          const id = await ensurePaperIdForAi();
                          if (id) setAiSheetOpen(true);
                        }}
                        variant="contained"
                        disabled={creatingForAi}
                        sx={{
                          backgroundColor: "#fcd307",
                          color: "#000000",
                          fontWeight: 700,
                          "&:hover": { backgroundColor: "#e6be06" },
                        }}
                      >
                        {creatingForAi ? "Saving..." : "Generate by AI"}
                      </Button>
                      <Button
                        onClick={handleResetQuestions}
                        variant="outlined"
                        disabled={!questionPaperId}
                        sx={{
                          color: "#fcd307",
                          borderColor: "rgba(252, 211, 7, 0.5)",
                          "&:hover": {
                            borderColor: "rgba(252, 211, 7, 0.85)",
                            backgroundColor: "rgba(252, 211, 7, 0.08)",
                          },
                        }}
                      >
                        Reset questions
                      </Button>
                    </Box>
                  </Box>
                ) : null}

                {formData.subject && questionBuildMode === "manual" ? (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: "#ffffff" }}>
                      Select Questions {errors.questions && <span style={{ color: "#ff0000" }}>*</span>}
                    </Typography>
                    {errors.questions && (
                      <Typography variant="caption" sx={{ color: "#ff0000", mb: 1, display: "block" }}>
                        {errors.questions}
                      </Typography>
                    )}

                    <Box
                      sx={{
                        maxHeight: 360,
                        overflow: "auto",
                        border: "1px solid rgba(255,255,255,0.35)",
                        p: 1,
                        borderRadius: 1,
                      }}
                    >
                      {availableQuestions.length === 0 ? (
                        <Box sx={{ textAlign: "center", py: 3 }}>
                          <Typography variant="body2" sx={{ color: "#ffffff", mb: 2 }}>
                            No questions found related to the subject
                          </Typography>
                          <Link
                            component="button"
                            variant="body2"
                            onClick={onGoToQuestionsPage}
                            sx={{
                              color: "#4CAF50",
                              textDecoration: "underline",
                              cursor: "pointer",
                              "&:hover": { color: "#66BB6A" },
                            }}
                          >
                            Go to Questions Page
                          </Link>
                        </Box>
                      ) : (
                        availableQuestions.map((question) => {
                          const questionIds =
                            formData.questions?.map((q: any) => (typeof q === "string" ? q : q._id)) || [];
                          const isSelected = questionIds.includes(question._id!);
                          return (
                            <FormControlLabel
                              key={question._id}
                              control={
                                <Checkbox
                                  checked={isSelected}
                                  onChange={() => onToggleQuestion(question._id!)}
                                  sx={{
                                    color: "#ffffff",
                                    "&.Mui-checked": { color: "#ffffff" },
                                  }}
                                />
                              }
                              label={
                                <Typography variant="body2" sx={{ maxWidth: 680, color: "#ffffff" }}>
                                  {question.question} ({question.marks} marks)
                                </Typography>
                              }
                              sx={{ color: "#ffffff" }}
                            />
                          );
                        })
                      )}
                    </Box>
                  </Box>
                ) : questionBuildMode === "manual" ? (
                  <Box
                    sx={{
                      border: "1px dashed rgba(255,255,255,0.22)",
                      borderRadius: 1.5,
                      p: 2,
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.82)", fontWeight: 700 }}>
                      Pick a subject to load questions
                    </Typography>
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)" }}>
                      The question bank appears here once a subject is selected.
                    </Typography>
                  </Box>
                ) : null}

                {selectedQuestions.length ? (
                  <Box
                    sx={{
                      border: "1px solid rgba(255,255,255,0.12)",
                      borderRadius: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 700 }}>
                        Selected Questions ({selectedQuestions.length})
                      </Typography>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>
                        You can edit or remove any question.
                      </Typography>
                    </Box>

                    <Box sx={{ maxHeight: 320, overflow: "auto" }}>
                      {selectedQuestions.map((q) => (
                        <Box
                          key={q._id}
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            gap: 1,
                            py: 1,
                            borderBottom: "1px solid rgba(255,255,255,0.08)",
                          }}
                        >
                          <Box sx={{ pr: 1 }}>
                            <Typography variant="body2" sx={{ color: "#ffffff" }}>
                              {q.question}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>
                              {q.marks} marks{q.negativeMarks ? ` • -${q.negativeMarks} negative` : ""} •{" "}
                              {q.difficultyLevel}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", justifyContent: "flex-end" }}>
                            <Button
                              onClick={() => openQuickEdit(q)}
                              size="small"
                              startIcon={<Edit fontSize="small" />}
                              sx={{
                                color: "#ffffff",
                                borderColor: "rgba(255,255,255,0.2)",
                                "&:hover": { backgroundColor: "rgba(255,255,255,0.06)" },
                              }}
                              variant="outlined"
                            >
                              Edit
                            </Button>
                            <Button
                              onClick={() => q._id && handleRemoveOne(q._id)}
                              size="small"
                              startIcon={<Delete fontSize="small" />}
                              sx={{
                                color: "#ff6b6b",
                                borderColor: "rgba(255,107,107,0.4)",
                                "&:hover": { backgroundColor: "rgba(255,107,107,0.08)" },
                              }}
                              variant="outlined"
                            >
                              Remove
                            </Button>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      border: "1px dashed rgba(255,255,255,0.18)",
                      borderRadius: 1.5,
                      p: 2,
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.82)", fontWeight: 700 }}>
                      No questions selected yet
                    </Typography>
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)" }}>
                      Select questions from the bank (Manual) or generate them (AI). You can also save an empty paper.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ backgroundColor: "#171717" }}>
          <Button
            onClick={onClose}
            sx={{
              color: "#fcd307",
              "&:hover": { backgroundColor: "rgba(252, 211, 7, 0.1)" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleFinalSubmit}
            variant="contained"
            disabled={!!loading}
            sx={{
              backgroundColor: "#fcd307",
              color: "#000000",
              "&:hover": { backgroundColor: "#e6be06" },
              "&:disabled": { backgroundColor: "#636363", color: "#ffffff" },
            }}
          >
            {submitText ?? (isEdit ? "Update" : "Create")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={marksConfirmOpen}
        onClose={() => setMarksConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#171717",
            color: "#ffffff",
            border: "1px solid rgba(255,255,255,0.12)",
          },
        }}
      >
        <DialogTitle sx={{ color: "#ffffff" }}>Marks don’t match</DialogTitle>
        <DialogContent sx={{ color: "#ffffff" }}>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.9)" }}>
            Your selected questions total <strong>{selectedTotalMarks}</strong> marks, but full marks is{" "}
            <strong>{fullMarks}</strong>.
          </Typography>
          <Box sx={{ mt: 1.25 }}>
            {markDelta > 0 ? (
              <Alert
                severity="info"
                sx={{
                  bgcolor: "rgba(33, 150, 243, 0.14)",
                  color: "#ffffff",
                  border: "1px solid rgba(33, 150, 243, 0.30)",
                  "& .MuiAlert-icon": { color: "rgba(33, 150, 243, 0.9)" },
                }}
              >
                You are <strong>{markDelta}</strong> marks under full marks.
              </Alert>
            ) : markDelta < 0 ? (
              <Alert
                severity="warning"
                sx={{
                  bgcolor: "rgba(255, 193, 7, 0.16)",
                  color: "#ffffff",
                  border: "1px solid rgba(255, 193, 7, 0.35)",
                  "& .MuiAlert-icon": { color: "rgba(255, 193, 7, 0.95)" },
                }}
              >
                You are <strong>{Math.abs(markDelta)}</strong> marks over full marks.
              </Alert>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: "#171717" }}>
          <Button
            onClick={() => setMarksConfirmOpen(false)}
            sx={{ color: "#fcd307", "&:hover": { backgroundColor: "rgba(252, 211, 7, 0.1)" } }}
          >
            Go back
          </Button>
          <Button
            onClick={() => {
              setMarksConfirmOpen(false);
              onSubmit();
            }}
            variant="contained"
            sx={{
              backgroundColor: "#fcd307",
              color: "#000000",
              "&:hover": { backgroundColor: "#e6be06" },
            }}
          >
            Proceed anyway
          </Button>
        </DialogActions>
      </Dialog>

      {canGenerateAi && aiTargetPaperId && signedAdmin ? (
        <AiQuestionPaperGeneratorSheet
          open={aiSheetOpen}
          onClose={() => setAiSheetOpen(false)}
          questionPaperId={aiTargetPaperId}
          subjects={subjects}
          defaultSubject={formData.subject}
          lockSubject={isPuzzleSolver}
          multiSubject={isMandatoryPaper}
          defaultClass={String(formData?.class || "")}
          lockClass={!!formData?.class}
          fullMarks={fullMarks}
          signedAdmin={signedAdmin}
          onGenerated={(paper) => {
            setFormData({ ...formData, questions: (paper as any).questions || [] });
            onPaperUpdated?.(paper);
          }}
        />
      ) : null}

      <Dialog
        open={quickEditOpen}
        onClose={() => {
          setQuickEditOpen(false);
          setQuickEditQuestion(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#171717",
            color: "#ffffff",
          },
        }}
      >
        <DialogTitle sx={{ color: "#ffffff" }}>Edit Question</DialogTitle>
        <DialogContent sx={{ backgroundColor: "#171717", color: "#ffffff" }}>
          {quickEditQuestion ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
              <TextField
                fullWidth
                label="Question"
                value={quickEditQuestion.question}
                onChange={(e) => setQuickEditQuestion({ ...quickEditQuestion, question: e.target.value })}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: "#ffffff",
                    "& fieldset": { borderColor: "rgba(255,255,255,0.45)" },
                    "&:hover fieldset": { borderColor: "rgba(255,255,255,0.75)" },
                    "&.Mui-focused fieldset": { borderColor: "#fcd307" },
                  },
                  "& .MuiInputLabel-root": { color: "#ffffff" },
                }}
              />

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                <TextField
                  type="number"
                  label="Marks"
                  value={quickEditQuestion.marks}
                  onChange={(e) => setQuickEditQuestion({ ...quickEditQuestion, marks: Number(e.target.value) })}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      color: "#ffffff",
                      "& fieldset": { borderColor: "rgba(255,255,255,0.45)" },
                      "&:hover fieldset": { borderColor: "rgba(255,255,255,0.75)" },
                      "&.Mui-focused fieldset": { borderColor: "#fcd307" },
                    },
                    "& .MuiInputLabel-root": { color: "#ffffff" },
                  }}
                />
                <TextField
                  type="number"
                  label="Negative marks"
                  value={quickEditQuestion.negativeMarks || 0}
                  onChange={(e) =>
                    setQuickEditQuestion({ ...quickEditQuestion, negativeMarks: Number(e.target.value) })
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      color: "#ffffff",
                      "& fieldset": { borderColor: "rgba(255,255,255,0.45)" },
                      "&:hover fieldset": { borderColor: "rgba(255,255,255,0.75)" },
                      "&.Mui-focused fieldset": { borderColor: "#fcd307" },
                    },
                    "& .MuiInputLabel-root": { color: "#ffffff" },
                  }}
                />
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {(quickEditQuestion.options || []).map((opt, idx) => (
                  <TextField
                    key={idx}
                    fullWidth
                    label={`Option ${String.fromCharCode(65 + idx)}`}
                    value={opt.optionText}
                    onChange={(e) => {
                      const next = [...(quickEditQuestion.options || [])];
                      next[idx] = { ...next[idx], optionText: e.target.value };
                      setQuickEditQuestion({ ...quickEditQuestion, options: next });
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        color: "#ffffff",
                        "& fieldset": { borderColor: "rgba(255,255,255,0.45)" },
                        "&:hover fieldset": { borderColor: "rgba(255,255,255,0.75)" },
                        "&.Mui-focused fieldset": { borderColor: "#fcd307" },
                      },
                      "& .MuiInputLabel-root": { color: "#ffffff" },
                    }}
                  />
                ))}
              </Box>

              <FormControl fullWidth>
                <InputLabel sx={{ color: "#ffffff" }}>Correct Option</InputLabel>
                <Select
                  value={(quickEditQuestion.correctOptionIndexes || [0])[0] ?? 0}
                  label="Correct Option"
                  onChange={(e) =>
                    setQuickEditQuestion({
                      ...quickEditQuestion,
                      correctOptionIndexes: [Number(e.target.value)],
                    })
                  }
                  sx={{
                    color: "#ffffff",
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.45)" },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.75)" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#fcd307" },
                    "& .MuiSvgIcon-root": { color: "#ffffff" },
                  }}
                >
                  <MenuItem value={0} sx={{ color: "#000000" }}>
                    A
                  </MenuItem>
                  <MenuItem value={1} sx={{ color: "#000000" }}>
                    B
                  </MenuItem>
                  <MenuItem value={2} sx={{ color: "#000000" }}>
                    C
                  </MenuItem>
                  <MenuItem value={3} sx={{ color: "#000000" }}>
                    D
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ backgroundColor: "#171717" }}>
          <Button
            onClick={() => {
              setQuickEditOpen(false);
              setQuickEditQuestion(null);
            }}
            sx={{ color: "#fcd307" }}
          >
            Cancel
          </Button>
          <Button
            onClick={saveQuickEdit}
            variant="contained"
            disabled={savingQuickEdit || !quickEditQuestion}
            sx={{
              backgroundColor: "#fcd307",
              color: "#000000",
              "&:hover": { backgroundColor: "#e6be06" },
              "&:disabled": { backgroundColor: "#636363", color: "#ffffff" },
            }}
          >
            {savingQuickEdit ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

type QuestionPaperViewDialogProps = PropsBase & {
  open: boolean;
  onClose: () => void;
  selectedPaper: any | null;
  primaryActionText?: string;
  onPrimaryAction?: () => void;
  primaryActionDisabled?: boolean;
  primaryActionColor?: "inherit" | "primary" | "secondary" | "success" | "error" | "info" | "warning";
};

export function QuestionPaperViewDialog({
  open,
  onClose,
  selectedPaper,
  primaryActionText,
  onPrimaryAction,
  primaryActionDisabled,
  primaryActionColor = "success",
}: QuestionPaperViewDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "#171717",
          color: "#ffffff",
        },
      }}
    >
      <DialogTitle sx={{ color: "#ffffff" }}>Question Paper Details</DialogTitle>
      <DialogContent sx={{ backgroundColor: "#171717", color: "#ffffff" }}>
        {selectedPaper && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ color: "#fcd307", mb: 0.5 }}>
                Title
              </Typography>
              <Typography variant="body1" sx={{ color: "#ffffff" }}>
                {selectedPaper.title}
              </Typography>
            </Box>

            {selectedPaper.description ? (
              <Box>
                <Typography variant="subtitle2" sx={{ color: "#fcd307", mb: 0.5 }}>
                  Description
                </Typography>
                <Typography variant="body1" sx={{ color: "#ffffff" }}>
                  {selectedPaper.description}
                </Typography>
              </Box>
            ) : null}

            <Box>
              <Typography variant="subtitle2" sx={{ color: "#fcd307", mb: 0.5 }}>
                Subject
              </Typography>
              <Typography variant="body1" sx={{ color: "#ffffff" }}>
                {selectedPaper.subject}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ color: "#fcd307", mb: 0.5 }}>
                  Exam Date & Time
                </Typography>
                <Typography variant="body1" sx={{ color: "#ffffff" }}>
                  {selectedPaper.examStartDateTime
                    ? new Date(selectedPaper.examStartDateTime).toLocaleString()
                    : "N/A"}
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ color: "#fcd307", mb: 0.5 }}>
                  Duration
                </Typography>
                <Typography variant="body1" sx={{ color: "#ffffff" }}>
                  {selectedPaper.durationInMinutes} minutes
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ color: "#fcd307", mb: 0.5 }}>
                  Full Marks
                </Typography>
                <Typography variant="body1" sx={{ color: "#ffffff" }}>
                  {selectedPaper.fullMarks}
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ color: "#fcd307", mb: 0.5 }}>
                  Pass Marks
                </Typography>
                <Typography variant="body1" sx={{ color: "#ffffff" }}>
                  {selectedPaper.passMarks}
                </Typography>
              </Box>
            </Box>

            {"isPublished" in selectedPaper ? (
              <Box>
                <Typography variant="subtitle2" sx={{ color: "#fcd307", mb: 1 }}>
                  Status
                </Typography>
                <Chip
                  label={selectedPaper.isPublished ? "Published" : "Draft"}
                  color={selectedPaper.isPublished ? "success" : "warning"}
                  size="small"
                  variant="filled"
                />
              </Box>
            ) : null}

            <Divider sx={{ borderColor: "#ffffff", my: 1 }} />

            <Box>
              <Typography variant="subtitle2" sx={{ color: "#fcd307", mb: 1 }}>
                Questions ({selectedPaper.questions?.length || 0})
              </Typography>
              <Box
                sx={{
                  maxHeight: 400,
                  overflow: "auto",
                  border: "1px solid #ffffff",
                  p: 2,
                  borderRadius: 1,
                }}
              >
                {selectedPaper.questions && selectedPaper.questions.length > 0 ? (
                  selectedPaper.questions.map((question: any, index: number) => {
                    const q = typeof question === "object" ? question : null;
                    return (
                      <Box
                        key={q?._id || index}
                        sx={{
                          mb: 2,
                          pb: 2,
                          borderBottom: "1px solid rgba(255,255,255,0.1)",
                        }}
                      >
                        <Typography variant="body2" sx={{ color: "#ffffff", fontWeight: "bold", mb: 0.5 }}>
                          Question {index + 1} ({q?.marks || "N/A"} marks)
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#ffffff" }}>
                          {q?.question || "Question not loaded"}
                        </Typography>
                        {q?.questionType === "quiz" && q?.options ? (
                          <Box sx={{ mt: 1, ml: 2 }}>
                            {q.options.map((option: any, optIndex: number) => (
                              <Typography
                                key={optIndex}
                                variant="body2"
                                sx={{
                                  color: q.correctOptionIndexes?.includes(optIndex) ? "#4caf50" : "#ffffff",
                                  fontWeight: q.correctOptionIndexes?.includes(optIndex) ? "bold" : "normal",
                                }}
                              >
                                {String.fromCharCode(65 + optIndex)}. {option.optionText}
                              </Typography>
                            ))}
                          </Box>
                        ) : null}
                      </Box>
                    );
                  })
                ) : (
                  <Typography variant="body2" sx={{ color: "#ffffff" }}>
                    No questions added yet
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ backgroundColor: "#171717" }}>
        {primaryActionText && onPrimaryAction ? (
          <Button
            onClick={onPrimaryAction}
            variant="contained"
            color={primaryActionColor}
            disabled={!!primaryActionDisabled}
            sx={{
              fontWeight: 800,
              ...(primaryActionColor === "success"
                ? {
                  backgroundColor: "#4caf50",
                  color: "#000000",
                  "&:hover": { backgroundColor: "#3ea846" },
                  "&:disabled": { backgroundColor: "#636363", color: "#ffffff" },
                }
                : {}),
            }}
          >
            {primaryActionText}
          </Button>
        ) : null}
        <Button
          onClick={onClose}
          sx={{
            color: "#fcd307",
            "&:hover": { backgroundColor: "rgba(252, 211, 7, 0.1)" },
          }}
        >
          {primaryActionText ? "Cancel" : "Close"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

